import { Layer, Panel, PanelType, Stack, ThemeProvider } from '@fluentui/react';
import assert from 'assert';
import ReactDOM from 'react-dom';
import ReactDOMServer from 'react-dom/server';

import { store } from '../../../App/store';
import { getAsync } from '../../../utils/api';
import { theme } from '../../../utils/theme';
import { AccountVersionData } from '../../Accounts/SelectAccount/Components/get-form-data';
import { FormVariables, getFormState } from '../slice/newFormReducers';
import Agreement from './Agreement';
import Checkboxes from './Checkboxes';
import CustomerInfo from './CustomerInfo';
import { Header } from './Header';
import PaymentsList from './PaymentsList';
import { ProductInfoList, ProductListWithHeader } from './ProductList';
import ShipmentInfo from './ShipmentInfo';
import { BorderedStack, Item, styles } from './utils';

type SellerInfo = {
  name?: string;
  phones?: string[];
  email?: string;
};

export type FormRenderConfig = {
  formVariables?: FormVariables;
  accountData?: AccountVersionData;
  sellerInfo?: SellerInfo;
  formId?: string;
  versionId?: string;
  isTemplate?: boolean;
};

export default class FormDocument {
  private formVariables?: FormVariables;
  private accountData?: AccountVersionData;
  private sellerInfo?: SellerInfo;
  private content?: JSX.Element;
  public formId?: string;
  public versionId?: string;

  constructor(public config?: FormRenderConfig) {
    if (config) {
      this.formVariables = config.formVariables;
      this.accountData = config.accountData;
      this.formId = config.formId;
      this.versionId = config.versionId;
      this.sellerInfo = config.sellerInfo;
    }

    if (!this.formVariables) {
      this.formVariables = getFormState();
    }

    if (!this.sellerInfo) {
      const state = store.getState();

      this.sellerInfo = {
        name: state.auth.userdata?.retail?.name,
        phones: state.auth.userdata?.retail?.phones,
        email:
          state.auth.userdata?.retail?.email ||
          state.auth.user?.email ||
          undefined,
      };
    }
  }

  async getContent() {
    if (this.content) {
      return this.content;
    }

    const { formVariables, formId, versionId } = this;
    assert(formVariables, "Could not get FormVariables");

    console.log({ formVariables, formId, versionId });

    const orderCreated = await getAsync("/no-auth/get-order-created?formId=" + (this.formVariables?.editingFormId ?? formId));
    const account = await this.getAccountData();

    const content = (
      <Stack>
        <div className={styles.container}>
          <div className={styles.line} />
          <div className={styles.body}>
            <ThemeProvider theme={theme}>
              <div>
                <Header
                  sellerName={this.sellerInfo?.name}
                  sellerEmail={this.sellerInfo?.email}
                  sellerPhones={this.sellerInfo?.phones}
                  serial={formVariables.serial}
                  formId={formId}
                  versionId={versionId}
                  isTemplate={this.config?.isTemplate}
                  orderCreated={new Date(orderCreated.data)}
                />
                <div className={styles.break} />
                <CustomerInfo accountVariables={account || {}} />
                <div className={styles.break} />
                <ProductListWithHeader formVariables={formVariables} />
                <div className="page-break" style={{ marginTop: "-1px" }}>
                  <Stack horizontal horizontalAlign="end">
                    <Stack
                      grow
                      tokens={{ childrenGap: 15 }}
                      style={{ padding: "8px", maxWidth: "45%" }}
                    >
                      <span
                        className="form-styles"
                        style={{ width: "100%", wordWrap: "break-word" }}
                      >
                        <b>Notlar:</b>{" "}
                        {<span>{formVariables.note}</span> || "-"}
                      </span>
                      {/* {
                        <span>
                          Satış Tarihi: {dateFormat(this.created, "dd.mm.yyyy")}
                        </span>
                      } */}
                      <Checkboxes formVariables={formVariables} />
                    </Stack>
                    <Stack.Item style={{ width: "50%" }}>
                      <ProductInfoList formVariables={formVariables} />
                    </Stack.Item>
                  </Stack>
                </div>
                {!this.config?.isTemplate && (
                  <>
                    <div className={styles.break} />
                    <div className="page-break">
                      <BorderedStack horizontal>
                        <Item style={{ width: "70%" }}>
                          <PaymentsList formVariables={formVariables} />
                        </Item>
                        <Item grow>
                          <ShipmentInfo formVariables={formVariables} />
                        </Item>
                      </BorderedStack>
                    </div>
                    <div className={styles.break} />
                    <div className="page-break">
                      <Agreement formVariables={formVariables} />
                    </div>
                  </>
                )}
                <div className={styles.break} />
                {formId && (
                  <Stack horizontalAlign="center">Evrak No: {formId}</Stack>
                )}
              </div>
            </ThemeProvider>
          </div>
        </div>
      </Stack>
    );

    this.content = content;
    return content;
  }

  async getAccountData() {
    if (this.accountData) {
      return this.accountData;
    }

    if (!this.formVariables || !this.formVariables.activeAccount) {
      return undefined;
    }

    const { id, versionId } = this.formVariables.activeAccount;
    const path = `/accounts/get?id=${id}&versionId=${versionId}`;
    const accountRes = await getAsync(path);
    assert(accountRes.success, "Could not get Account");

    const accountData = accountRes.data as AccountVersionData;
    this.accountData = accountData;

    return accountData;
  }

  async preview() {
    const content = await this.getContent();
    const div = document.createElement("div");
    document.body.append(div);

    const closeModal = () => {
      ReactDOM.unmountComponentAtNode(div);
      div.remove();
      console.log("panel unmount");
    };

    ReactDOM.render(
      <Layer>
        <Panel
          isOpen
          isLightDismiss
          type={PanelType.largeFixed}
          styles={{ scrollableContent: { overflowX: "hidden" } }}
          onDismiss={closeModal}
        >
          <div
            style={{
              width: "min-content",
              height: "80vh",
              padding: ".5rem",
              border: `1px solid ${theme.palette.neutralTertiaryAlt}`,
            }}
          >
            {content}
          </div>
        </Panel>
      </Layer>,
      div
    );
  }

  async print() {
    const content = await this.getContent();
    const formHTML = ReactDOMServer.renderToString(content);

    const children = Array.from(document.body.children);
    const hiddenElements: Element[] = [];

    for (let i = 0; i < children.length; i++) {
      const element = children[i];
      element.classList.add("print-display-none");
      hiddenElements.push(element);
    }

    const div = document.createElement("div");
    div.classList.add("print-display-unset");
    document.body.append(div);

    div.innerHTML = formHTML;

    await new Promise((res) => setTimeout(res, 2500));

    window.print();

    div.remove();
    hiddenElements.forEach((el) => el.classList.remove("print-display-none"));
  }

  async renderAsBody() {
    const content = await this.getContent();
    const formHTML = ReactDOMServer.renderToString(content);

    document.body.innerHTML = formHTML;
  }
}
